import { useEffect, useMemo, useState } from 'react'
import GeoJSON from 'geojson'
import { Point } from 'mapbox-gl'

import { InjectionTokens } from '@/controller/tokens'

import { isKnownAirspaceType } from '@/domain/protocols/Airspaces'
import { MapLayerState, MapLayerStateProps } from '@/domain/states/MapLayerState'

import { IAirspacesRepository } from '@/data/AirspacesRepository/IAirspacesRepository'

import {
  getAirspaceMenuLabel,
  orderAirspaceTypesForRender,
  slugifyAirspaceType
} from '../components/MapAirspace/airspaceTypeRegistry'
import { MapActiveAirspace } from '../components/MapAirspace/MapActiveAirspace'
import { MapAirspace } from '../components/MapAirspace/MapAirspace'
import { ignoreMapClickLayers } from '../components/MapView/constants'
import { useMapContext } from '../components/MapView/MapContext'
import { MapImperativeInterface } from '../interfaces/MapImperativeInterface'
import { FeatureInfoState, FeatureInfoStateMutator } from '../states/FeatureInfoState'
import { MeasuringSegmentState } from '../states/MapMeasuringSegmentState'

import { useInjection } from '@/presentation/contexts/InjectionContext'
import { useBehaviorSubject } from '@/presentation/hooks/useBehaviorSubject'
import { useGlobalState } from '@/presentation/hooks/useGlobalState'
import { useI18n } from '@/presentation/hooks/useI18n'

interface SelectedAirspaceType {
  airspaceType: string
  code: string
}

type MapFeatures = Record<string, GeoJSON.FeatureCollection<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>>

function MapAirspacesPresenter() {
  const { t } = useI18n()

  const mapContext = useMapContext()

  const airspaceRepository = useInjection<IAirspacesRepository>(InjectionTokens.AirspaceRepository)
  const [mapLayerState] = useGlobalState<MapLayerState, MapLayerStateProps>(InjectionTokens.MapLayerState)
  const featureInfoState = useBehaviorSubject(FeatureInfoState)
  const measuringSegmentState = useBehaviorSubject(MeasuringSegmentState)

  const makeAirspaceTitle = (name: string, type?: string): string => {
    switch (type) {
      case 'adiz':
        return `${t('AIRSPACES-INFO_FEATURE-TITLE_ADIZ')} ${name}`
      case 'afis':
        return `${t('AIRSPACES-INFO_FEATURE-TITLE_AFIS')} ${name}`
      case 'atz':
        return `${t('AIRSPACES-INFO_FEATURE-TITLE_ATZ')} ${name}`
      case 'cta':
        return `${t('AIRSPACES-INFO_FEATURE-TITLE_CTA')} ${name}`
      case 'ctaSector':
        return `${t('AIRSPACES-INFO_FEATURE-TITLE_CTA-SECTOR')} ${name}`
      case 'ctr':
        return `${t('AIRSPACES-INFO_FEATURE-TITLE_CTR')} ${name}`
      case 'freeRouteAreas':
        return `${t('AIRSPACES-INFO_FEATURE-TITLE_FREE-ROUTE-AREAS')} ${name}`
      case 'fis':
        return `${t('AIRSPACES-INFO_FEATURE-TITLE_FIS')} ${name}`
      case 'fir':
        return `${t('AIRSPACES-INFO_FEATURE-TITLE_FIR')} ${name}`
      case 'firSector':
        return `${t('AIRSPACES-INFO_FEATURE-TITLE_FIR-SECTOR')} ${name}`
      case 'fiz':
        return `${t('AIRSPACES-INFO_FEATURE-TITLE_FIZ')} ${name}`
      case 'fcaAd':
        return `${t('AIRSPACES-INFO_FEATURE-TITLE_FCA_AD')} ${name}`
      case 'fca':
        return `${t('AIRSPACES-INFO_FEATURE-TITLE_FCA_REA')} ${name}`
      case 'fcaReh':
        return `${t('AIRSPACES-INFO_FEATURE-TITLE_FCA_REH')} ${name}`
      case 'suaDangerous':
        return `${t('AIRSPACES-INFO_FEATURE-TITLE_DANGER-AREA')} ${name}`
      case 'suaProhibited':
        return `${t('AIRSPACES-INFO_FEATURE-TITLE_PROHIBITED-AREA')} ${name}`
      case 'suaRestricted':
        return `${t('AIRSPACES-INFO_FEATURE-TITLE_RESTRICTED-AREA')} ${name}`
      case 'tma':
        return `${t('AIRSPACES-INFO_FEATURE-TITLE_TMA')} ${name}`
      case 'tmaSector':
        return `${t('AIRSPACES-INFO_FEATURE-TITLE_TMA-SECTOR')} ${name}`
      default:
        // Tipos não catalogados: rotula pelo tipo cru em maiúsculas.
        return type ? `${getAirspaceMenuLabel(type)} ${name}` : name
    }
  }

  const [selectedAirspace, setSelectedAirspace] = useState<SelectedAirspaceType>(null)

  const renderTypes = useMemo(
    () => orderAirspaceTypesForRender(airspaceRepository.getAvailableTypes()),
    [airspaceRepository]
  )

  const mapFeatures = useMemo(() => {
    const features: MapFeatures = {}
    renderTypes.forEach((type) => {
      const collection = airspaceRepository.getAll(type)
      if (collection.features.length > 0) features[type] = collection
    })

    return features
  }, [renderTypes, airspaceRepository])

  const isAirspaceActive = (type: string): boolean =>
    isKnownAirspaceType(type)
      ? !!mapLayerState[type as keyof MapLayerStateProps]
      : !!mapLayerState.airspaceExtras?.[type]

  useEffect(() => {
    if (featureInfoState.active?.type !== 'airspace') {
      setSelectedAirspace(null)
    }
  }, [featureInfoState.active])

  const activeAirspace = useMemo(() => {
    if (selectedAirspace) {
      return airspaceRepository.getByCode(selectedAirspace.airspaceType, selectedAirspace.code)
    }

    return null
  }, [selectedAirspace, airspaceRepository])

  useEffect(() => {
    if (activeAirspace) {
      if (activeAirspace.bbox) {
        MapImperativeInterface.flyToBounds(
          activeAirspace.bbox.slice(0, 4) as [number, number, number, number],
          [150, 100, 150, 100],
          500
        )
      }

      FeatureInfoStateMutator.showAirspace(
        makeAirspaceTitle(activeAirspace.properties.name ?? '', selectedAirspace?.airspaceType),
        selectedAirspace.airspaceType,
        activeAirspace
      )
    } else if (FeatureInfoState.getValue().active?.type === 'airspace') {
      // Only clear the panel when it is actually showing an airspace. Without this
      // guard, a remount (e.g. after a spontaneous style reload) runs this effect
      // with `activeAirspace === null` and would close ANY open info panel
      // (navaid, obstacle, fuel, geometry) — not just the airspace one.
      FeatureInfoStateMutator.hide()
    }
  }, [activeAirspace])

  const handleAirspacePress = (
    airspaceType: string,
    feature: GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>,
    point: Point
  ) => {
    if (measuringSegmentState.active) return

    const ignoredLayers: string[] = []

    mapContext.map.getStyle().layers.forEach((layer) => {
      if (ignoreMapClickLayers.filter((layer) => layer.includes('fuel')).includes(layer.id)) {
        ignoredLayers.push(layer.id)
      }
    })
    const features = mapContext.map.queryRenderedFeatures(point, {
      layers: ignoredLayers
    })
    if (features.length > 0) return

    setSelectedAirspace((prev) => {
      if (prev?.code === feature.properties?.code && prev?.airspaceType === airspaceType) {
        return null
      }

      return { airspaceType, code: feature.properties?.code }
    })
  }

  const getAirspaceId = (airspaceName: string, type: 'source' | 'layer') => {
    const slug = slugifyAirspaceType(airspaceName)
    if (type === 'source') return `airspace-${slug}-source`
    else return `airspace-${slug}-layer`
  }

  return (
    <>
      {renderTypes.map(
        (airspaceName: string) =>
          isAirspaceActive(airspaceName) &&
          mapFeatures[airspaceName] && (
            <MapAirspace
              key={airspaceName}
              airspaceId={airspaceName}
              borderSourceId={`${getAirspaceId(airspaceName, 'source')}-border`}
              borderLayerId={`${getAirspaceId(airspaceName, 'layer')}-border`}
              symbolSourceId={`${getAirspaceId(airspaceName, 'source')}-symbol`}
              symbolLayerId={`${getAirspaceId(airspaceName, 'layer')}-symbol`}
              features={mapFeatures[airspaceName]}
              onPress={(feature: GeoJSON.Feature<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>, point: Point) =>
                handleAirspacePress(airspaceName, feature, point)
              }
            />
          )
      )}
      {selectedAirspace && activeAirspace && (
        <MapActiveAirspace
          type={selectedAirspace.airspaceType}
          feature={activeAirspace}
          fillSourceId={`airspace-source-fill`}
          fillLayerId={`airspace-layer-fill`}
        />
      )}
    </>
  )
}

export { MapAirspacesPresenter }
